import { useEffect, useMemo, useState } from 'react';
import { SitePlanItem, UpcomingProject } from '../types';
import {
  fetchTopViewingSitePlans,
  toSitePlanItem,
  toUpcomingProject,
} from '../services/superPickerPublicApi';

interface UseSitePlansResult {
  upcoming: UpcomingProject[];
  sitePlans: SitePlanItem[];
  loading: boolean;
  error: string | null;
}

/**
 * Loads the list of site plans from
 * `GET /api/super-picker-public/top-viewing-site-plans` and partitions them
 * into `upcoming` and regular `sitePlans` for the UI.
 *
 * On request failure the lists stay empty and `error` is populated — the UI
 * surfaces the error banner at the top of the page rather than substituting
 * bundled mock data (which could mislead the user into thinking the page is
 * working).
 */
export function useSitePlans(): UseSitePlansResult {
  const [upcoming, setUpcoming] = useState<UpcomingProject[]>([]);
  const [sitePlans, setSitePlans] = useState<SitePlanItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const raw = await fetchTopViewingSitePlans();
        if (cancelled) return;
        setUpcoming(raw.filter((r) => r.upcoming).map(toUpcomingProject));
        setSitePlans(raw.filter((r) => !r.upcoming).map(toSitePlanItem));
      } catch (err) {
        if (cancelled) return;
        console.error('[useSitePlans] failed to load site plans:', err);
        setUpcoming([]);
        setSitePlans([]);
        setError(err instanceof Error ? err.message : 'Failed to load site plans');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(
    () => ({ upcoming, sitePlans, loading, error }),
    [upcoming, sitePlans, loading, error],
  );
}
